import { Injectable } from '@angular/core';
import { FieldType } from '@sb-tools/typescript/enums/field-type.enum';
import { ClientField } from '../models/client-field.model';
import { Field } from '@sb-tools/typescript/models/field.model';
import { ClientFieldRule } from '../models/client-field-rule.model';
import { FieldRuleValidationType } from '@sb-tools/typescript/enums/field-rule-validation-type.enum';

@Injectable({
    providedIn: 'root',
})
export class FieldRulesValidationService {
    private static checkIsChecked(rule: ClientFieldRule, field: ClientField | Field): boolean {
        return rule.ruleValues[0] === String(!!field.value);
    }

    private static checkIsContains(rule: ClientFieldRule, field: ClientField | Field): boolean {
        const value = this.getStringLowerCaseValue(field);

        return value.includes(rule.ruleValues[0]);
    }

    private static checkIsContainsAllOf(rule: ClientFieldRule, field: ClientField | Field): boolean {
        const valuesList = this.getValuesList(field.value);

        return rule.ruleValues.every((ruleValue) => {
            return valuesList.includes(ruleValue);
        });
    }

    private static checkIsContainsAnyOf(rule: ClientFieldRule, field: ClientField | Field): boolean {
        const valuesList = this.getValuesList(field.value);

        return rule.ruleValues.some((ruleValue) => {
            return valuesList.includes(ruleValue);
        });
    }

    private static checkIsContainsNoneOf(rule: ClientFieldRule, field: ClientField | Field): boolean {
        const valuesList = this.getValuesList(field.value);

        return valuesList.length && !this.checkIsContainsAnyOf(rule, field);
    }

    private static checkIsEqualsInAnyOrder(rule: ClientFieldRule, field: ClientField | Field): boolean {
        return rule.ruleValues.length === field.valueModel?.length && this.checkIsContainsAllOf(rule, field);
    }

    private static checkIsEqualTo(rule: ClientFieldRule, field: ClientField | Field): boolean {
        const value = this.isHtmlEditorValue(field)
            ? field.valueModel.textValue?.toLowerCase()
            : this.getStringLowerCaseValue(field);

        return rule.ruleValues[0] === value;
    }

    private static checkIsNotContains(rule: ClientFieldRule, field: ClientField | Field): boolean {
        const value = this.getStringLowerCaseValue(field);

        return value && !this.checkIsContains(rule, field);
    }

    private static checkIsNotEqualTo(rule: ClientFieldRule, field: ClientField | Field): boolean {
        const value = this.isHtmlEditorValue(field)
            ? field.valueModel.textValue?.toLowerCase()
            : this.getStringLowerCaseValue(field);

        return value && !this.checkIsEqualTo(rule, field);
    }

    private static checkIsNotEmpty(rule: ClientFieldRule, field: ClientField | Field): boolean {
        return !!String(field.value || '');
    }

    private static checkIsNotOneOf(rule: ClientFieldRule, field: ClientField | Field): boolean {
        const value = this.getStringLowerCaseValue(field);

        return value && !this.checkIsOneOf(rule, field);
    }

    private static checkIsOneOf(rule: ClientFieldRule, field: ClientField | Field): boolean {
        const value = this.getStringLowerCaseValue(field);

        return rule.ruleValues.includes(value);
    }

    private static checkIsStartsWith(rule: ClientFieldRule, field: ClientField | Field): boolean {
        const value = this.getStringLowerCaseValue(field);

        return value.startsWith(rule.ruleValues[0]);
    }

    public static isFieldValueMatchRule(rule: ClientFieldRule, field: ClientField | Field): boolean {
        switch (rule.validationType) {
            case FieldRuleValidationType.equalTo: {
                return this.checkIsEqualTo(rule, field);
            }
            case FieldRuleValidationType.notEqualTo: {
                return this.checkIsNotEqualTo(rule, field);
            }
            case FieldRuleValidationType.isOneOf: {
                return this.checkIsOneOf(rule, field);
            }
            case FieldRuleValidationType.isNotOneOf: {
                return this.checkIsNotOneOf(rule, field);
            }
            case FieldRuleValidationType.containsAnyOf: {
                return this.checkIsContainsAnyOf(rule, field);
            }
            case FieldRuleValidationType.containsAllOf: {
                return this.checkIsContainsAllOf(rule, field);
            }
            case FieldRuleValidationType.containsNoneOf: {
                return this.checkIsContainsNoneOf(rule, field);
            }
            case FieldRuleValidationType.equalsInAnyOrder: {
                return this.checkIsEqualsInAnyOrder(rule, field);
            }
            case FieldRuleValidationType.isNotEmpty: {
                return this.checkIsNotEmpty(rule, field);
            }
            case FieldRuleValidationType.startsWith: {
                return this.checkIsStartsWith(rule, field);
            }
            case FieldRuleValidationType.contains: {
                return this.checkIsContains(rule, field);
            }
            case FieldRuleValidationType.notContains: {
                return this.checkIsNotContains(rule, field);
            }
            case FieldRuleValidationType.isChecked: {
                return this.checkIsChecked(rule, field);
            }
        }
    }

    private static getStringLowerCaseValue(field: ClientField | Field): string {
        return String(field.value || '')
            .toLowerCase()
            .trim();
    }

    private static getValuesList(fieldValue = ''): string[] {
        if (fieldValue) {
            return fieldValue.split(',').map((value) => {
                return value.toLowerCase().trim();
            });
        } else {
            return [];
        }
    }

    private static isHtmlEditorValue(field: ClientField | Field): boolean {
        return field.type === FieldType.message && field.options?.isHtmlEditor === 'true';
    }
}
